import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import JobDetailsModal from "@/components/JobDetailsModal";
import ReviewModal from "@/components/ReviewModal";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { toast } from "sonner";
import { 
  Calendar, 
  MapPin,
  DollarSign,
  Clock,
  Briefcase,
  MessageCircle,
  Eye,
  Loader2,
  TrendingUp,
  Phone,
  Mail,
  User as UserIcon,
  Building2,
  Star,
  CreditCard,
  X
} from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";
import "@/styles/commandDeck.css";

const UpcomingBookings = ({ onLogout }) => {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('shootzu_user') || '{}');
  
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showJobDetails, setShowJobDetails] = useState(false);
  const [fullJobData, setFullJobData] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewingJob, setReviewingJob] = useState(null);
  const [selectedCrewMember, setSelectedCrewMember] = useState(null);
  const [showCrewSelector, setShowCrewSelector] = useState(false);

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/bookings/upcoming`,
        {
          params: { user_email: user.email || 'test@shootzu.com' }
        }
      );
      
      setBookings(response.data.bookings || []);
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
      toast.error('Failed to load upcoming bookings');
      setBookings([]);
    } finally {
      setLoading(false);
    }
  }, [user.email]);

  const handleMessage = useCallback(async (otherEmail, jobId, jobTitle) => {
    console.log('handleMessage called:', { otherEmail, jobId, jobTitle, userEmail: user.email });
    
    if (!otherEmail) {
      console.error('Missing other party email');
      toast.error('Unable to contact - email not available');
      return;
    }
    
    if (!user.email) {
      console.error('Missing user email');
      toast.error('Please log in to send messages');
      return;
    }
    
    try {
      console.log('Creating conversation...');
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/messages/conversation/create`,
        {
          job_id: jobId,
          job_title: jobTitle,
          user1_email: user.email,
          user2_email: otherEmail
        }
      );
      
      console.log('Conversation response:', response.data);
      
      // Get conversation ID from response
      const conversation = response.data.conversation;
      const conversationId = response.data.conversation_id || conversation?.id || conversation?._id;
      
      if (conversationId || conversation) {
        toast.success('Opening conversation...');
        // Navigate to messages with conversation ID
        navigate('/messages', { 
          state: { 
            conversationId: conversationId || conversation.id,
            autoOpen: true 
          } 
        });
      } else {
        console.error('No conversation data in response:', response.data);
        toast.error('Failed to create conversation');
      }
    } catch (error) {
      console.error('Error creating conversation:', error);
      console.error('Error details:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to start conversation');
    }
  }, [user.email, navigate]);

  const handleViewDetails = useCallback(async (jobId, booking) => {
    try {
      setSelectedBooking(booking); // Store the booking for message context
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/jobs/details/${jobId}`
      );
      setFullJobData(response.data);
      setShowJobDetails(true);
    } catch (error) {
      console.error('Failed to fetch job details:', error);
      toast.error('Failed to load job details');
    }
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return 'TBD';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    const dayOfWeek = date.toLocaleString('en-US', { weekday: 'short' });
    return { day, month, year, dayOfWeek, fullDate: `${day} ${month} ${year}` };
  };

  const getCrewPayout = (amount) => {
    return Math.floor(amount * 0.98);
  };

  const getDaysUntil = (dateString) => {
    if (!dateString) return null;
    // Use Melbourne timezone for consistent countdown
    const melbTZ = 'Australia/Melbourne';
    const todayMelb = new Date(new Date().toLocaleString('en-US', { timeZone: melbTZ }));
    const eventMelb = new Date(new Date(dateString).toLocaleString('en-US', { timeZone: melbTZ }));
    // Zero out time to compare dates only
    todayMelb.setHours(0, 0, 0, 0);
    eventMelb.setHours(0, 0, 0, 0);
    const diffTime = eventMelb - todayMelb;
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gradient-to-br from-[#0a0118] via-[#1a1625] to-[#0f0a1f]' : 'bg-gradient-to-br from-gray-50 via-white to-gray-100'}`}>
      <Sidebar onLogout={onLogout} />
      <Header />

      <div className="cd-root sidebar-content pt-16 lg:pt-20">
        {/* Ambient blooms */}
        <div className="cd-bloom-wrap">
          <div className="cd-bloom cd-bloom-1" />
          <div className="cd-bloom cd-bloom-2" />
        </div>

        <div className="cd-main">
          {/* Header row */}
          <div className="cd-col-mobile" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 20, marginBottom: 22 }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span className="cd-display" style={{ fontWeight: 700, fontSize: 11, letterSpacing: 2, color: '#67C6B4' }}>LOCKED IN</span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontWeight: 700, fontSize: 11.5, color: '#67C6B4', background: 'rgba(103,198,180,.16)', padding: '4px 11px', borderRadius: 999 }}>
                  <TrendingUp className="w-3 h-3" />
                  {bookings.length} {bookings.length === 1 ? 'booking' : 'bookings'}
                </span>
              </div>
              <div className="cd-display" style={{ fontWeight: 800, fontSize: 32, letterSpacing: '-1px', color: '#fff', marginTop: 8 }}>Upcoming bookings</div>
            </div>
          </div>

          {/* States */}
          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="cd-skel">
                  <span style={{ width: 90, height: 64, borderRadius: 12, background: 'rgba(255,255,255,.08)', flex: '0 0 auto' }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ width: 120, height: 11, borderRadius: 6, background: 'rgba(255,255,255,.08)', marginBottom: 10 }} />
                    <div style={{ width: '55%', height: 16, borderRadius: 6, background: 'rgba(255,255,255,.1)', marginBottom: 10 }} />
                    <div style={{ width: '38%', height: 10, borderRadius: 6, background: 'rgba(255,255,255,.07)' }} />
                  </div>
                  <div style={{ width: 80, height: 36, borderRadius: 10, background: 'rgba(255,255,255,.08)', flex: '0 0 auto' }} />
                </div>
              ))}
            </div>
          ) : bookings.length === 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: '60px 20px', background: 'rgba(255,255,255,.025)', border: '1px solid rgba(255,255,255,.08)', borderRadius: 18 }}>
              <span style={{ width: 64, height: 64, borderRadius: 18, background: 'rgba(40,192,208,.12)', color: '#28C0D0', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 18 }}>
                <Calendar className="w-[30px] h-[30px]" />
              </span>
              <div className="cd-display" style={{ fontWeight: 700, fontSize: 19, color: '#fff' }}>No upcoming bookings</div>
              <div style={{ fontSize: 13.5, color: 'rgba(255,255,255,.55)', marginTop: 7, maxWidth: 340, lineHeight: 1.55 }}>
                Accepted offers and confirmed jobs will appear here.
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 20, flexWrap: 'wrap', justifyContent: 'center' }}>
                <button type="button" onClick={() => navigate('/find-jobs')} className="cd-btn cd-btn-primary" style={{ padding: '12px 22px', fontSize: 13.5 }}>
                  Find jobs
                </button>
                <button type="button" onClick={() => navigate('/hire-crew')} className="cd-btn" style={{ padding: '12px 22px', fontSize: 13.5 }}>
                  Post a job
                </button>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {bookings.map((booking) => {
                const dateInfo = formatDate(booking.date);
                const daysUntil = getDaysUntil(booking.date);
                const isUpcoming = daysUntil > 0;
                const isToday = daysUntil === 0;
                const isPast = daysUntil < 0;

                // Countdown strip palette
                let stripColor = '#67C6B4', stripBg = 'rgba(103,198,180,.08)';
                if (isToday) { stripColor = '#F87171'; stripBg = 'rgba(185,28,28,.14)'; }
                else if (isUpcoming && daysUntil <= 3) { stripColor = '#FCD34D'; stripBg = 'rgba(180,83,9,.12)'; }
                else if (isPast) { stripColor = 'rgba(255,255,255,.45)'; stripBg = 'rgba(255,255,255,.04)'; }

                const isCrew = booking.user_role === 'crew';
                const filledNames = booking.is_advanced_job && booking.crew_members?.length > 0
                  ? (booking.crew_members
                      .filter(cm => cm.hired_user_id || cm.hiredUserName)
                      .map(cm => cm.hiredUserName || cm.hired_name)
                      .filter(Boolean)
                      .join(', ') || booking.other_party_name)
                  : booking.other_party_name;

                return (
                  <div
                    key={booking.id}
                    className="cd-card cd-col-mobile"
                    onClick={() => handleViewDetails(booking.id, booking)}
                    style={{
                      cursor: 'pointer',
                      ...(isToday ? { border: '1px solid rgba(103,198,180,.4)', boxShadow: '0 0 0 1px rgba(103,198,180,.15)', background: 'rgba(103,198,180,.06)' } : {})
                    }}
                  >
                    {/* Countdown strip */}
                    <div className="cd-strip" style={{ background: stripBg }}>
                      {isToday ? (
                        <>
                          <div className="cd-strip-num" style={{ color: stripColor, fontSize: 24 }}>TODAY</div>
                          <div className="cd-strip-label" style={{ color: stripColor }}>LET&rsquo;S GO</div>
                        </>
                      ) : isUpcoming ? (
                        <>
                          <div className="cd-strip-num" style={{ color: stripColor }}>{daysUntil}</div>
                          <div className="cd-strip-label" style={{ color: stripColor }}>{daysUntil === 1 ? 'DAY TO GO' : 'DAYS TO GO'}</div>
                        </>
                      ) : (
                        <>
                          <div className="cd-strip-num" style={{ color: stripColor }}>{Math.abs(daysUntil)}</div>
                          <div className="cd-strip-label" style={{ color: stripColor }}>{Math.abs(daysUntil) === 1 ? 'DAY AGO' : 'DAYS AGO'}</div>
                        </>
                      )}
                    </div>

                    {/* Body */}
                    <div className="cd-col-mobile" style={{ flex: 1, minWidth: 0, padding: '18px 20px', display: 'flex', alignItems: 'center', gap: 18 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {/* Badge row */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap', marginBottom: 7 }}>
                          {isCrew ? (
                            <span className="cd-pill" style={{ color: '#28C0D0', background: 'rgba(40,192,208,.16)', fontWeight: 700 }}>Crew</span>
                          ) : (
                            <span className="cd-pill" style={{ color: '#C98AD4', background: 'rgba(120,40,124,.28)', fontWeight: 700 }}>Your job</span>
                          )}
                          {booking.crew_role && isCrew && (
                            <span className="cd-pill" style={{ textTransform: 'capitalize' }}>
                              {booking.crew_role.replace(/_/g, ' ')}
                            </span>
                          )}
                          {booking.is_advanced_job && !isCrew && (
                            <span className="cd-pill" style={{ color: '#67C6B4', background: 'rgba(103,198,180,.18)', fontWeight: 700 }}>
                              {booking.crew_members?.filter(cm => cm.hired_user_id || cm.hiredUserName).length || 0}/{booking.crew_members?.length || 0} crew
                            </span>
                          )}
                          {booking.status === 'RELEASE_PAYMENT' && (
                            <span className="cd-badge" style={{ color: '#FCD34D', background: 'rgba(180,83,9,.22)' }}>
                              <span className="cd-badge-dot" style={{ background: '#FCD34D' }} />
                              Payment processing
                            </span>
                          )}
                          {booking.status === 'PENDING_COMPLETION' && (
                            <span className="cd-pill" style={{ color: 'rgba(255,255,255,.6)' }}>Pending review</span>
                          )}
                          <span className="cd-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,.5)' }}>
                            {dateInfo.dayOfWeek}, {dateInfo.fullDate}
                          </span>
                        </div>

                        {/* Title */}
                        <div className="cd-display" style={{ fontWeight: 700, fontSize: 18, color: '#fff' }}>{booking.title}</div>

                        {/* Meta */}
                        <div style={{ display: 'flex', gap: 16, marginTop: 8, flexWrap: 'wrap', color: 'rgba(255,255,255,.6)', fontSize: 13 }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Clock className="w-3.5 h-3.5" />{booking.hours}h
                          </span>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <MapPin className="w-3.5 h-3.5" />{booking.location?.split(',')[0] || 'Location TBD'}
                          </span>
                          {filledNames && (
                            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span style={{ color: 'rgba(255,255,255,.4)' }}>{isCrew ? 'POSTED BY:' : 'HIRED CREW:'}</span>
                              {filledNames}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Payment */}
                      <div style={{ textAlign: 'right', flex: '0 0 auto' }}>
                        <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,.5)' }}>{isCrew ? 'Your payout' : 'Total'}</div>
                        <div className="cd-display" style={{ fontWeight: 800, fontSize: 24, letterSpacing: '-.5px', lineHeight: 1.1, color: isCrew ? '#67C6B4' : '#fff' }}>
                          ${isCrew
                            ? (booking.crew_payout
                                ? Math.floor(booking.crew_payout).toLocaleString()
                                : getCrewPayout(booking.total_amount || booking.budget || 0).toLocaleString())
                            : (booking.total_amount || booking.budget || booking.crew_members?.[0]?.subtotal || 0).toLocaleString()}
                        </div>
                        <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,.5)' }}>
                          ${isCrew ? getCrewPayout(booking.pay_rate) : booking.pay_rate}/hr · {booking.hours}h
                        </div>
                      </div>

                      {/* Actions */}
                      <div style={{ display: 'flex', gap: 9, flex: '0 0 auto' }}>
                        {!(booking.is_advanced_job && booking.user_role === 'business' &&
                          booking.crew_members?.filter(cm => cm.hired_user_id || cm.hiredUserName).length > 1) && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMessage(booking.other_party_email, booking.id, booking.title);
                            }}
                            className="cd-btn"
                            style={{ width: 42, height: 42, padding: 0, justifyContent: 'center', background: '#059669', borderColor: 'transparent', color: '#fff' }}
                            aria-label="Message"
                          >
                            <MessageCircle className="w-[18px] h-[18px]" />
                          </button>
                        )}

                        {booking.status === 'PENDING_COMPLETION' && booking.user_role === 'business' && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setReviewingJob(booking);
                              if (booking.is_advanced_job && booking.crew_members?.length > 0) {
                                const unreleasedCrew = booking.crew_members.filter(
                                  cm => (cm.hired_user_id || cm.hiredUserName) && !cm.payment_released
                                );
                                if (unreleasedCrew.length === 1) {
                                  const cm = unreleasedCrew[0];
                                  setSelectedCrewMember({
                                    userId: cm.hired_user_id,
                                    name: cm.hired_name || cm.hiredUserName || 'Unknown',
                                    email: cm.hiredUserEmail || cm.hired_email,
                                    role: cm.role || 'Crew Member',
                                    hours: cm.hours || 0,
                                    ratePerHour: cm.ratePerHour || 0,
                                    subtotal: cm.subtotal || 0,
                                    crewPayout: cm.crew_payout || Math.round((cm.subtotal || 0) * 0.98 * 100) / 100
                                  });
                                  setShowReviewModal(true);
                                } else if (unreleasedCrew.length > 1) {
                                  setShowCrewSelector(true);
                                } else {
                                  setShowReviewModal(true);
                                }
                              } else {
                                setShowReviewModal(true);
                              }
                            }}
                            className="cd-btn cd-btn-primary"
                            style={{ background: '#15803D', color: '#fff', boxShadow: 'none' }}
                          >
                            <Star className="w-3.5 h-3.5" />
                            Release
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Job Details Modal */}
      {showJobDetails && fullJobData && selectedBooking && (
        <JobDetailsModal
          job={{
            ...fullJobData,
            // Override with booking-specific data for crew viewing their role in an advanced job
            // This ensures crew only see their specific role info, not other positions
            crew_members: selectedBooking.crew_members || fullJobData.crew_members,
            crewMembers: selectedBooking.crew_members || fullJobData.crewMembers,
            is_advanced_job: selectedBooking.is_advanced_job,
            hours: selectedBooking.hours || fullJobData.hours,
            pay_rate: selectedBooking.pay_rate || fullJobData.pay_rate,
            ratePerHour: selectedBooking.pay_rate || fullJobData.ratePerHour,
            total_amount: selectedBooking.total_amount || fullJobData.total_amount,
            total: selectedBooking.total_amount || fullJobData.total,
            budget: selectedBooking.budget || fullJobData.budget,
            crew_count: selectedBooking.crew_count || fullJobData.crew_count,
            user_role: selectedBooking.user_role,
            crew_payout: selectedBooking.crew_payout,
            crew_role: selectedBooking.crew_role,
            owner: selectedBooking.owner || fullJobData.owner
          }}
          isOpen={showJobDetails}
          onClose={() => {
            setShowJobDetails(false);
            setFullJobData(null);
            setSelectedBooking(null);
          }}
          onMessageOwner={() => handleMessage(selectedBooking.other_party_email, fullJobData.id, fullJobData.title)}
          onMessageCrewMember={(crewEmail) => handleMessage(crewEmail, fullJobData.id, fullJobData.title)}
          isDark={isDark}
          showCrewPayout={selectedBooking.user_role === 'crew'}
          isJobOwner={selectedBooking.user_role === 'business'}
        />
      )}

      {/* Review Modal for Release Payment */}
      {showReviewModal && reviewingJob && (
        <ReviewModal
          job={reviewingJob}
          crewMember={selectedCrewMember}
          isOpen={showReviewModal}
          onClose={() => {
            setShowReviewModal(false);
            setReviewingJob(null);
            setSelectedCrewMember(null);
          }}
          onComplete={() => {
            setShowReviewModal(false);
            setReviewingJob(null);
            setSelectedCrewMember(null);
            fetchBookings(); // Refresh bookings
            toast.success('Payment released and review submitted!');
          }}
          isDark={isDark}
        />
      )}

      {/* Crew Selector Modal for Advanced Jobs */}
      {showCrewSelector && reviewingJob && (
        <div className="cd-root" style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(8,11,20,.66)', backdropFilter: 'blur(5px)' }} onClick={() => { setShowCrewSelector(false); setReviewingJob(null); }} />
          <div style={{ position: 'relative', width: '100%', maxWidth: 460, maxHeight: '90vh', overflowY: 'auto', background: '#12162A', border: '1px solid rgba(255,255,255,.12)', borderRadius: 22, boxShadow: '0 40px 90px -30px rgba(0,0,0,.85)' }}>
            {/* Header */}
            <div style={{ position: 'relative', padding: '24px 26px 18px', borderBottom: '1px solid rgba(255,255,255,.08)' }}>
              <button
                onClick={() => { setShowCrewSelector(false); setReviewingJob(null); }}
                style={{ position: 'absolute', top: 16, right: 16, width: 34, height: 34, borderRadius: 10, background: 'rgba(255,255,255,.12)', border: '1px solid rgba(255,255,255,.18)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
              >
                <X className="w-[17px] h-[17px]" />
              </button>
              <div className="cd-display" style={{ fontWeight: 800, fontSize: 20, color: '#fff', letterSpacing: '-.4px' }}>
                Select crew member
              </div>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,.6)', marginTop: 5, lineHeight: 1.5 }}>
                Choose who to review &amp; release payment for on <span style={{ color: '#fff' }}>{reviewingJob.title}</span>.
              </div>
            </div>

            {/* Crew List */}
            <div style={{ padding: '18px 22px', display: 'flex', flexDirection: 'column', gap: 10, maxHeight: '60vh', overflowY: 'auto' }}>
              {reviewingJob.crew_members
                ?.filter(cm => cm.hired_user_id || cm.hiredUserName)
                .map((cm, idx) => {
                  const isReleased = !!cm.payment_released;
                  const name = cm.hired_name || cm.hiredUserName || 'Unknown';
                  const role = cm.role || 'Crew Member';
                  const hours = cm.hours || 0;
                  const rate = cm.ratePerHour || 0;
                  const subtotal = cm.subtotal || 0;

                  return (
                    <button
                      key={cm.hired_user_id || idx}
                      disabled={isReleased}
                      onClick={() => {
                        setSelectedCrewMember({
                          userId: cm.hired_user_id,
                          name,
                          email: cm.hiredUserEmail || cm.hired_email,
                          role,
                          hours,
                          ratePerHour: rate,
                          subtotal,
                          crewPayout: cm.crew_payout || Math.round(subtotal * 0.98 * 100) / 100
                        });
                        setShowCrewSelector(false);
                        setShowReviewModal(true);
                      }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 12, padding: '13px 14px',
                        border: '1px solid rgba(255,255,255,.1)', borderRadius: 13, textAlign: 'left',
                        background: 'transparent',
                        cursor: isReleased ? 'not-allowed' : 'pointer',
                        opacity: isReleased ? 0.5 : 1
                      }}
                    >
                      <span style={{ width: 40, height: 40, borderRadius: 999, background: 'linear-gradient(135deg,#572B7C,#28C0D0)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontFamily: 'Poppins, sans-serif', fontWeight: 700, fontSize: 12, flex: '0 0 auto' }}>
                        {name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                      </span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13.5, fontWeight: 600, color: '#fff' }}>{name}</div>
                        <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,.5)' }}>
                          {role} &middot; {hours}h @ ${rate}/hr
                        </div>
                      </div>
                      <div style={{ textAlign: 'right', flex: '0 0 auto' }}>
                        <div style={{ fontSize: 13.5, fontWeight: 700, color: '#67C6B4' }}>${subtotal.toLocaleString()}</div>
                        {isReleased ? (
                          <span style={{ fontSize: 10.5, fontWeight: 700, color: '#67C6B4', background: 'rgba(103,198,180,.16)', padding: '2px 8px', borderRadius: 999 }}>Released</span>
                        ) : (
                          <span style={{ fontSize: 10.5, fontWeight: 700, color: '#FCD34D', background: 'rgba(180,83,9,.22)', padding: '2px 8px', borderRadius: 999 }}>Ready</span>
                        )}
                      </div>
                    </button>
                  );
                })}
            </div>

            {/* Footer */}
            <div style={{ padding: '14px 22px', borderTop: '1px solid rgba(255,255,255,.08)' }}>
              <button
                type="button"
                onClick={() => { setShowCrewSelector(false); setReviewingJob(null); }}
                className="cd-btn"
                style={{ width: '100%', justifyContent: 'center' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UpcomingBookings;
